// context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load from localStorage on app start
  useEffect(() => {
    const userData = localStorage.getItem('user');
    const authToken = localStorage.getItem('token');
    if (userData !== null && authToken !== null && userData !== 'undefined' && authToken !== 'undefined') {
      const parsedUser = JSON.parse(userData);
      // The club selection is app-added and session-scoped: never trust it from
      // localStorage. Restore it from sessionStorage so it survives a refresh but
      // not a new tab / reopened browser / fresh login.
      delete parsedUser.selectedClub;
      const sessionClub = sessionStorage.getItem('selectedClub');
      if (sessionClub && sessionClub !== 'undefined') {
        try {
          const club = JSON.parse(sessionClub);
          if (club && club.id != null) {
            parsedUser.selectedClub = club;
            parsedUser.issuerId = club.id;
            parsedUser.clubName = club.name;
          }
        } catch (e) {
          sessionStorage.removeItem('selectedClub');
        }
      }
      setUser(parsedUser);
      setToken(authToken);
    }
    setLoading(false);
  }, []);

  const login = (userData,userEntryName, authToken) => {
    // A fresh login starts with no club selected.
    sessionStorage.removeItem('selectedClub');
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('token', authToken);
    setUser(userData);
    setToken(authToken);
  };

  const setSelectedClub = (club) => {
    if (!club) {
      return;
    }
    // Persist the selection for this browser session only (not localStorage) so
    // it cannot linger into a new visit.
    sessionStorage.setItem('selectedClub', JSON.stringify(club));
    setUser((currentUser) => {
      if (!currentUser) {
        return currentUser;
      }
      return {
        ...currentUser,
        selectedClub: club,
        issuerId: club.id,
        clubName: club.name,
      };
    });
  };

  const clearSelectedClub = () => {
    sessionStorage.removeItem('selectedClub');
    setUser((currentUser) => {
      if (!currentUser) {
        return currentUser;
      }

      const updatedUser = { ...currentUser };
      delete updatedUser.selectedClub;
      delete updatedUser.issuerId;
      delete updatedUser.clubName;

      localStorage.setItem('user', JSON.stringify(updatedUser));
      return updatedUser;
    });
  };

  const logout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    sessionStorage.removeItem('selectedClub');
    setUser(null);
    setToken(null);
  };

  const value = {
    user,
    token,
    login,
    logout,
    setSelectedClub,
    clearSelectedClub,
    isAuthenticated: !!user && !!token,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
